import { z } from 'zod';
import { DocumentCategoryGroupSchema, DocumentCategorySchema } from '../property/documents.schemas';

/**
 * One row of the admin document-analysis Skills list (IMM-406.2). Overlays the
 * persisted `immo_admin.skills` rows onto the complete analysable taxonomy, so
 * every analysable category yields a row. `status` carries `'unconfigured'` for a
 * category with no stored skill; the frontend maps the three tokens to the three
 * German labels. The backend returns only tokens — the German skill/group names
 * are resolved on the frontend from `@immo/i18n`.
 */
export const DocumentAnalysisSkillListItemSchema = z.object({
  category: DocumentCategorySchema,
  group: DocumentCategoryGroupSchema,
  status: z.enum(['active', 'inactive', 'unconfigured']),
  lastEditedByName: z.string().min(1).nullable(),
  lastEditedAt: z.iso.datetime().nullable(),
});

export type DocumentAnalysisSkillListItem = z.infer<typeof DocumentAnalysisSkillListItemSchema>;

export const ListDocumentAnalysisSkillsResponseSchema = z.object({
  data: z.array(DocumentAnalysisSkillListItemSchema),
});

export type ListDocumentAnalysisSkillsResponse = z.infer<typeof ListDocumentAnalysisSkillsResponseSchema>;

/** The maximum length of a managed prompt (IMM-406.3 Resolved Decision). */
export const MAX_ANALYSIS_PROMPT_LENGTH = 10_000;

/**
 * The editor view of a single skill (IMM-406.3). `prompt`/`version` are null for
 * an unconfigured type; `effectiveDefault` is always present and drives the
 * pre-fill + baseline marker. `version` is the optimistic-concurrency token the
 * client echoes back on save.
 */
export const DocumentAnalysisSkillViewSchema = z.object({
  category: DocumentCategorySchema,
  status: z.enum(['active', 'inactive', 'unconfigured']),
  prompt: z.string().nullable(),
  effectiveDefault: z.string(),
  version: z.number().int().nullable(),
  lastEditedByName: z.string().min(1).nullable(),
  lastEditedAt: z.iso.datetime().nullable(),
});

export type DocumentAnalysisSkillView = z.infer<typeof DocumentAnalysisSkillViewSchema>;

export const DocumentAnalysisSkillPathParamsSchema = z.object({
  category: DocumentCategorySchema,
});

export const SaveDocumentAnalysisSkillPromptBodySchema = z.object({
  prompt: z.string(),
  // null configures a new skill (create + activate); a number edits an existing
  // one and is the stale-edit token.
  expectedVersion: z.number().int().nullable(),
});

export type SaveDocumentAnalysisSkillPromptBody = z.infer<typeof SaveDocumentAnalysisSkillPromptBodySchema>;

export const SetDocumentAnalysisSkillStatusBodySchema = z.object({
  expectedVersion: z.number().int(),
});

export type SetDocumentAnalysisSkillStatusBody = z.infer<typeof SetDocumentAnalysisSkillStatusBodySchema>;

/**
 * The system fallback skill's editor/list view (IMM-469.2). It has no `category`
 * (it applies to every document without a matching category skill) and no
 * `'unconfigured'` / `'inactive'` status — the row is seeded active and guarded
 * against deactivation, so `status` is the literal `'active'`. `prompt`/`version`
 * are non-nullable, unlike {@link DocumentAnalysisSkillViewSchema}, because the
 * IMM-469.1 seed guarantees the row exists.
 */
export const SystemSkillViewSchema = z.object({
  status: z.literal('active'),
  prompt: z.string(),
  version: z.number().int(),
  lastEditedByName: z.string().min(1).nullable(),
  lastEditedAt: z.iso.datetime(),
});

export type SystemSkillView = z.infer<typeof SystemSkillViewSchema>;

export const SaveSystemSkillPromptBodySchema = z.object({
  prompt: z.string(),
  // Never null — the seeded row always exists, so a save is always an edit and
  // the version is always the stale-edit token.
  expectedVersion: z.number().int(),
});

export type SaveSystemSkillPromptBody = z.infer<typeof SaveSystemSkillPromptBodySchema>;
